import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { GooglePlacesAutocomplete } from '@/components/admin/GooglePlacesAutocomplete';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calendar,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  MoreHorizontal,
  MapPin,
  Video,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Database,
  RefreshCw,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Helper function to safely format date
const formatEventDate = (event) => {
  const dateStr = event.event_date || event.date;
  if (!dateStr) return 'No date';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return 'Invalid Date';
  }
};

const STATUS_BADGES = {
  upcoming: { label: 'Upcoming', color: 'bg-blue-500' },
  ongoing: { label: 'Ongoing', color: 'bg-green-500' },
  completed: { label: 'Completed', color: 'bg-gray-500' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500' }
};

const APPROVAL_BADGES = {
  draft: { label: 'Draft', color: 'bg-gray-500' },
  pending_review: { label: 'Pending Review', color: 'bg-amber-500' },
  approved: { label: 'Approved', color: 'bg-green-500' },
  changes_requested: { label: 'Changes Requested', color: 'bg-orange-500' },
  rejected: { label: 'Rejected', color: 'bg-red-500' }
};

const EVENT_TYPES = {
  webinar: 'Webinar',
  workshop: 'Workshop',
  fair: 'Education Fair',
  seminar: 'Seminar',
  open_day: 'Open Day',
  other: 'Other'
};

export const EventsManager = () => {
  const { token, user, logout } = useCMSAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [tradeShows, setTradeShows] = useState([]);
  const [tradeShowsLoading, setTradeShowsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'webinar',
    event_date: '',
    event_time: '',
    location: '',
    venue_address: '',
    google_maps_url: '',
    is_online: false,
    registration_link: '',
    featured_image: '',
    max_attendees: '',
    tags: '',
    universities: '',
    highlights: '',
    speaker: '',
    speaker_role: '',
    status: 'upcoming',
    trade_show_id: '',
    trade_show_name: ''
  });

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  const fetchEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '15' });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('event_type', typeFilter);
      
      const res = await fetch(`${API_URL}/api/cms/content/events?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (handleUnauthorized(res)) return;
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events);
        setTotalPages(data.pages);
        setTotal(data.total);
      }
    } catch (e) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [token, page, search, statusFilter, typeFilter, logout]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Fetch trade shows from Zoho when dialog opens
  const [tradeShowError, setTradeShowError] = useState('');
  
  const fetchTradeShows = useCallback(async () => {
    setTradeShowsLoading(true);
    setTradeShowError('');
    try {
      const res = await fetch(`${API_URL}/api/cms/content/zoho/trade-shows`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setTradeShows(data.trade_shows || []);
        } else {
          setTradeShowError(data.error || 'Failed to fetch trade shows');
          setTradeShows([]);
        }
      }
    } catch (e) {
      console.error('Failed to fetch trade shows:', e);
      setTradeShowError('Network error fetching trade shows');
    } finally {
      setTradeShowsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (dialogOpen) {
      fetchTradeShows();
    }
  }, [dialogOpen, fetchTradeShows]);

  const handleSave = async () => {
    // Trim whitespace from text fields
    const title = formData.title?.trim() || '';
    const description = formData.description?.trim() || '';
    const event_date = formData.event_date?.trim() || '';
    
    // Debug log
    console.log('Saving event with data:', { title, description, event_date, fullFormData: formData });
    
    // Validate required fields
    const missingFields = [];
    if (!title) missingFields.push('Title');
    if (!description) missingFields.push('Description');
    if (!event_date) missingFields.push('Date');
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }
    
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        title,
        description,
        event_date,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
        universities: formData.universities ? formData.universities.split(',').map(u => u.trim()).filter(u => u) : [],
        highlights: formData.highlights ? formData.highlights.split('\n').map(h => h.trim()).filter(h => h) : []
      };
      
      const url = selectedEvent 
        ? `${API_URL}/api/cms/content/events/${selectedEvent.id}`
        : `${API_URL}/api/cms/content/events`;
      
      const res = await fetch(url, {
        method: selectedEvent ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(selectedEvent ? 'Event updated' : 'Event created');
        setDialogOpen(false);
        resetForm();
        fetchEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save event');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/content/events/${selectedEvent.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Event deleted');
        setDeleteDialogOpen(false);
        setSelectedEvent(null);
        fetchEvents();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) {
      toast.error('Failed to delete event');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      event_type: event.event_type,
      event_date: event.event_date,
      event_time: event.event_time || '',
      location: event.location || '',
      venue_address: event.venue_address || '',
      google_maps_url: event.google_maps_url || '',
      is_online: event.is_online || false,
      registration_link: event.registration_link || '',
      featured_image: event.featured_image || '',
      max_attendees: event.max_attendees?.toString() || '',
      tags: event.tags?.join(', ') || '',
      universities: event.universities?.join(', ') || '',
      highlights: event.highlights?.join('\n') || '',
      speaker: event.speaker || '',
      speaker_role: event.speaker_role || '',
      status: event.status,
      trade_show_id: event.trade_show_id || '',
      trade_show_name: event.trade_show_name || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_type: 'webinar',
      event_date: '',
      event_time: '',
      location: '',
      venue_address: '',
      google_maps_url: '',
      is_online: false,
      registration_link: '',
      featured_image: '',
      max_attendees: '',
      tags: '',
      universities: '',
      highlights: '',
      speaker: '',
      speaker_role: '',
      status: 'upcoming',
      trade_show_id: '',
      trade_show_name: ''
    });
    setSelectedEvent(null);
  };

  const handleSeedData = async () => {
    if (!confirm('This will replace all existing events with seed data. Continue?')) return;
    
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/content/events/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Events seeded successfully!');
        fetchEvents();
      } else {
        toast.error('Failed to seed data');
      }
    } catch (error) {
      console.error('Error seeding:', error);
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  const canEdit = ['super_admin', 'admin', 'content_approver', 'content_editor'].includes(user?.role);
  const canDelete = ['super_admin', 'admin', 'content_approver'].includes(user?.role);
  const canApprove = ['super_admin', 'admin', 'content_approver'].includes(user?.role);

  const handleApprovalAction = async (eventId, action, comment = null) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/content/events/${eventId}/approval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action, comment })
      });
      
      if (res.ok) {
        const actionLabels = {
          submit_for_review: 'Submitted for review',
          approve: 'Approved',
          reject: 'Rejected',
          request_changes: 'Changes requested'
        };
        toast.success(actionLabels[action] || 'Action completed');
        fetchEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (e) {
      toast.error('Failed to perform action');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="events-manager">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ux-charcoal flex items-center gap-2">
              <Calendar className="w-7 h-7 text-ux-coral" />
              Events
            </h1>
            <p className="text-ux-charcoal/60">
              Manage webinars, workshops, and fairs
            </p>
          </div>
          
          {canEdit && (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={handleSeedData}
                disabled={seeding}
              >
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                Seed Data
              </Button>
              <Button 
                className="bg-ux-coral hover:bg-ux-coral/90"
                onClick={() => { resetForm(); setDialogOpen(true); }}
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Event
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ux-charcoal/40" />
                <Input
                  placeholder="Search events..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="ongoing">Ongoing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(EVENT_TYPES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Events Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && <TableHead className="w-16">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-ux-charcoal/60">
                      No events found
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{event.title}</p>
                          <p className="text-xs text-ux-charcoal/60 truncate max-w-xs">
                            {event.description?.substring(0, 80)}...
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{EVENT_TYPES[event.event_type]}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatEventDate(event)}
                        {event.event_time && <span className="text-ux-charcoal/60"> {event.event_time}</span>}
                      </TableCell>
                      <TableCell>
                        {event.is_online ? (
                          <span className="flex items-center gap-1 text-sm">
                            <Video className="w-4 h-4 text-blue-500" /> Online
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-sm">
                            <MapPin className="w-4 h-4 text-ux-coral" /> {event.location || '-'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge className={`${STATUS_BADGES[event.status]?.color} text-white text-xs`}>
                            {STATUS_BADGES[event.status]?.label}
                          </Badge>
                          {event.approval_status && event.approval_status !== 'approved' && (
                            <Badge className={`${APPROVAL_BADGES[event.approval_status]?.color} text-white text-xs`}>
                              {APPROVAL_BADGES[event.approval_status]?.label}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(event)}>
                                <Edit className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/admin/events/${event.id}/registrations`)}>
                                <Users className="w-4 h-4 mr-2" /> View Registrations
                              </DropdownMenuItem>
                              
                              {/* Approval workflow actions */}
                              {(!event.approval_status || event.approval_status === 'draft' || event.approval_status === 'changes_requested') && (
                                <DropdownMenuItem 
                                  onClick={() => handleApprovalAction(event.id, 'submit_for_review')}
                                  className="text-blue-600"
                                >
                                  <Send className="w-4 h-4 mr-2" /> Submit for Review
                                </DropdownMenuItem>
                              )}
                              
                              {canApprove && event.approval_status === 'pending_review' && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleApprovalAction(event.id, 'approve')}
                                    className="text-green-600"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2" /> Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      const comment = prompt('Enter reason for requesting changes:');
                                      if (comment) handleApprovalAction(event.id, 'request_changes', comment);
                                    }}
                                    className="text-orange-600"
                                  >
                                    <Clock className="w-4 h-4 mr-2" /> Request Changes
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      const comment = prompt('Enter reason for rejection:');
                                      if (comment) handleApprovalAction(event.id, 'reject', comment);
                                    }}
                                    className="text-red-600"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" /> Reject
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              {canDelete && (
                                <DropdownMenuItem 
                                  onClick={() => { setSelectedEvent(event); setDeleteDialogOpen(true); }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-ux-charcoal/60">
              Showing {((page - 1) * 15) + 1} - {Math.min(page * 15, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1 text-sm">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" aria-describedby="event-dialog-description">
            <DialogHeader>
              <DialogTitle>{selectedEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
              <p id="event-dialog-description" className="sr-only">
                {selectedEvent ? 'Edit the details of an existing event' : 'Create a new event by filling in the required fields'}
              </p>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(d => ({ ...d, title: e.target.value }))}
                  placeholder="Event title"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(d => ({ ...d, description: e.target.value }))}
                  placeholder="Event description..."
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Event Type *</Label>
                  <Select value={formData.event_type} onValueChange={(v) => setFormData(d => ({ ...d, event_type: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EVENT_TYPES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData(d => ({ ...d, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="ongoing">Ongoing</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Event Date *</Label>
                  <Input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData(d => ({ ...d, event_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Event Time</Label>
                  <Input
                    type="time"
                    value={formData.event_time}
                    onChange={(e) => setFormData(d => ({ ...d, event_time: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.is_online}
                  onCheckedChange={(v) => setFormData(d => ({ ...d, is_online: v }))}
                />
                <Label>Online Event</Label>
              </div>
              {!formData.is_online && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Location / City</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData(d => ({ ...d, location: e.target.value }))}
                      placeholder="e.g. Delhi"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      Venue Address 
                      <span className="text-xs text-muted-foreground font-normal">(search Google Maps)</span>
                    </Label>
                    <GooglePlacesAutocomplete
                      value={formData.venue_address}
                      onChange={(address) => setFormData(d => ({ ...d, venue_address: address }))}
                      onPlaceSelect={(place) => {
                        if (place) {
                          setFormData(d => ({ 
                            ...d, 
                            venue_address: place.formatted_address || place.address,
                            google_maps_url: place.google_maps_url
                          }));
                        }
                      }}
                      placeholder="Search for venue location..."
                    />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Registration Link</Label>
                  <Input
                    value={formData.registration_link}
                    onChange={(e) => setFormData(d => ({ ...d, registration_link: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>Max Attendees</Label>
                  <Input
                    type="number"
                    value={formData.max_attendees}
                    onChange={(e) => setFormData(d => ({ ...d, max_attendees: e.target.value }))}
                    placeholder="Leave empty for unlimited"
                  />
                </div>
              </div>
              <div>
                <Label>Featured Image URL</Label>
                <Input
                  value={formData.featured_image}
                  onChange={(e) => setFormData(d => ({ ...d, featured_image: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData(d => ({ ...d, tags: e.target.value }))}
                    placeholder="e.g. USA, MBA, Scholarships"
                  />
                </div>
                <div>
                  <Label>Participating Universities (comma-separated)</Label>
                  <Input
                    value={formData.universities}
                    onChange={(e) => setFormData(d => ({ ...d, universities: e.target.value }))}
                    placeholder="e.g. MIT, Stanford, Harvard"
                  />
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  Key Highlights 
                  <span className="text-xs text-muted-foreground font-normal">(one per line)</span>
                </Label>
                <Textarea
                  value={formData.highlights}
                  onChange={(e) => setFormData(d => ({ ...d, highlights: e.target.value }))}
                  placeholder="Enter key highlights, one per line:&#10;Meet top universities from 10+ countries&#10;On-spot application fee waivers&#10;Free profile evaluation"
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Speaker Name</Label>
                  <Input
                    value={formData.speaker}
                    onChange={(e) => setFormData(d => ({ ...d, speaker: e.target.value }))}
                    placeholder="e.g. John Smith"
                  />
                </div>
                <div>
                  <Label>Speaker Role/Title</Label>
                  <Input
                    value={formData.speaker_role}
                    onChange={(e) => setFormData(d => ({ ...d, speaker_role: e.target.value }))}
                    placeholder="e.g. Admissions Director"
                  />
                </div>
              </div>
              
              {/* Zoho Trade Show Integration */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2 text-base font-semibold">
                    Zoho Trade Show Integration
                    <Badge variant="outline" className="text-xs">Optional</Badge>
                  </Label>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    onClick={fetchTradeShows}
                    disabled={tradeShowsLoading}
                    className="h-8 text-xs"
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${tradeShowsLoading ? 'animate-spin' : ''}`} />
                    Refresh from Zoho
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Link this event to a Trade Show in Zoho CRM for registration sync
                </p>
                <div>
                  <Label>Trade Show (Active only)</Label>
                  <Select 
                    value={formData.trade_show_id || 'none'} 
                    onValueChange={(v) => {
                      if (v === 'none') {
                        setFormData(d => ({ ...d, trade_show_id: '', trade_show_name: '' }));
                      } else {
                        const show = tradeShows.find(t => t.id === v);
                        setFormData(d => ({ 
                          ...d, 
                          trade_show_id: v, 
                          trade_show_name: show?.name || '' 
                        }));
                      }
                    }}
                  >
                    <SelectTrigger className={tradeShowsLoading ? 'opacity-50' : ''}>
                      <SelectValue placeholder={tradeShowsLoading ? "Loading..." : "Select a trade show"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {tradeShows.map(show => (
                        <SelectItem key={show.id} value={show.id}>
                          {show.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.trade_show_name && !formData.trade_show_id && (
                    <p className="text-xs text-ux-cyan mt-1">
                      Manual entry: {formData.trade_show_name}
                    </p>
                  )}
                  {formData.trade_show_id && formData.trade_show_name && (
                    <p className="text-xs text-ux-cyan mt-1">
                      Linked to: {formData.trade_show_name}
                    </p>
                  )}
                  {tradeShowError && (
                    <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs">
                      <p className="text-amber-600 mb-1">{tradeShowError}</p>
                      <p className="text-muted-foreground">Enter trade show name manually below:</p>
                    </div>
                  )}
                  {(tradeShows.length === 0 || tradeShowError) && (
                    <div className="mt-2">
                      <Label className="text-xs">Manual Trade Show Name</Label>
                      <Input
                        value={formData.trade_show_name}
                        onChange={(e) => setFormData(d => ({ ...d, trade_show_name: e.target.value, trade_show_id: '' }))}
                        placeholder="e.g. Test Event March"
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedEvent ? 'Save Changes' : 'Create Event'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent aria-describedby="delete-dialog-description">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Event</DialogTitle>
            </DialogHeader>
            <p id="delete-dialog-description">Are you sure you want to delete "{selectedEvent?.title}"?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default EventsManager;
